import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import { cloneBot, createNewBotAPI, getAIModels } from '../api/bots'
import { assignOrChangeBotPackage } from '../api/botPackage'
import { cacheBotPackageAssignment } from '../helpers/orgPackages'
import { alertError } from '../../../api'
import { connect } from 'react-redux'

import LoaderSmall from '../../../uiKit/loaders/loaderSmall'
import Input from '../../../uiKit/inputs/Input'
import Select from '../../../uiKit/Select/Select'
import SubmitButton from '../../../uiKit/buttons/SubmitButton'
import Modal from '../../../uiKit/Modal'
import CloneBotConfirmModal from './CloneBotConfirmModal'
import TierSelect from './TierSelect'
import { isStringEmpty } from '../../../helpers/isStringEmpty'

const styles = theme => ({
  group: {
    marginLeft: 10,
  },
  description: {
    fontSize: 12,
    color: 'var(--color-spun-pearl-grey)',
    fontFamily: 'Lato',
    marginBottom: 0,
  },
  root: {
    width: '100%',
    marginTop: 16,
    '& span': {
      fontSize: 14,
      color: 'var(--color-text-secondary)',
      fontFamily: 'Lato',
    },
    '& label': {
      margin: '-4px 16px -4px -14px',
    },
    '& span[class*="label"]': {
      marginLeft: 8,
    },
    '&>div>label>span[class*="checked"]>span>svg': {
      color: 'var(--color-button-primary)',
    },
    '& span svg': {
      color: 'var(--color-mischka-grey)',
    },
    '& span svg path': {
      stroke: '1px solid var(--color-mischka-grey)',
    },
  },
  borderLine: {
    borderBottom: '1px solid var(--color-issabeline-grey)',
    lineHeight: 0,
    margin: '24px 0 0',
  },
  modalHeading: {
    fontSize: '14px',
    lineHeight: '17px',
    fontFamily: 'Lato, "sans-serif"',
    color: 'var(--color-text-primary)',
    margin: '24px 0 4px',
  },
  error: {
    color: theme.defaults.error,
    marginBottom: '-10px',
    fontSize: 12,
  },
  buttonHolder: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: 32,
    gap: '10px',
  },
  buttonHolderCreate: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32,
  },
  aiModelSelect: {
    marginTop: 16,
  },
  confirmModalPaper: {
    padding: '0!important',
    width: '540px!important',
  },
})

class NewBotModal extends React.Component {
  /** Home "New organisation" / "New QCS organisation" — not the legacy clone flow. */
  isOrganisationCreate = () => Boolean(this.props.new)

  /** CareBrain create and clone need an AI model; QCS create does not. */
  needsAiModel = () => !this.props.isQcs || !this.isOrganisationCreate()

  checkInputFields = () => {
    let botError = false
    let botErrorType = 0
    let botErrorText = ''
    const { name, clientToken, developerToken, tier } = this.state
    const isCreate = this.isOrganisationCreate()
    const isQcsCreate = isCreate && this.props.isQcs
    const needsAiModel = this.needsAiModel()

    if (name.includes('/')) {
      botError = true
      botErrorText = "Name field can't contain '/'"
    } else if (isStringEmpty(name)) {
      botError = true
      botErrorText = "Name field can't be empty!"
    } else if (this.checkBotName()) {
      botError = true
      botErrorText = isCreate
        ? 'An organisation with this name already exists.'
        : 'Chatbot with such name already exists!'
    } else if (isQcsCreate && isStringEmpty(tier)) {
      botError = true
      botErrorText = 'Please choose a package!'
    } else if (needsAiModel && !this.state.selectedAiModel) {
      botError = true
      botErrorText = 'Please select an AI model!'
    } else if (this.state.withDialogFlow === 'true') {
      if (isStringEmpty(clientToken)) {
        botError = true
        botErrorType = 1
        botErrorText = 'DialogFlow client token cannot be an empty field!'
      } else if (isStringEmpty(developerToken)) {
        botError = true
        botErrorType = 2
        botErrorText = 'DialogFlow developer token cannot be an empty field!'
      }
    }

    const aiModelError = needsAiModel && !this.state.selectedAiModel

    this.setState({
      botError,
      botErrorType,
      botErrorText,
      aiModelError,
    })
    return !botError && !aiModelError
  }

  toggleLoader = () => {
    this.setState({ showLoader: !this.state.showLoader })
  }

  errorHandler = text => {
    this.setState({
      botError: true,
      botErrorText: text,
      showLoader: false,
    })
  }

  checkBotName = () => {
    const normalize = str => str.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    return this.props.bots.find(
      bot => normalize(bot.name) === normalize(this.state.name),
    )
  }

  constructor(props) {
    super(props)
    this.state = {
      name: props.botName,
      botError: false,
      botErrorText: '',
      withDialogFlow: 'false',
      clientToken: '',
      developerToken: '',
      showLoader: false,
      buttonName: props.buttonName,
      aiModels: [],
      selectedAiModel: props.aiModel
        ? { label: props.aiModel.modelName, value: props.aiModel.id }
        : null,
      aiModelError: false,
      showConfirmModal: false,
      // Package tier for QCS organisation create only.
      tier: '',
    }
  }

  componentDidMount() {
    // QCS create has no AI field; CareBrain create and clone both need models.
    if (this.isOrganisationCreate() && this.props.isQcs) return
    getAIModels(models => {
      this.setState({ aiModels: Array.isArray(models) ? models : [] })
    })
  }

  onSubmit() {
    if (!this.checkInputFields()) {
      return
    }

    if (!this.props.new) {
      this.setState({ showConfirmModal: true })
      return
    }

    this.executeSubmit()
  }

  closeConfirmModal = () => {
    this.setState({ showConfirmModal: false })
  }

  handleConfirmClone = () => {
    this.setState({ showConfirmModal: false }, () => this.executeSubmit())
  }

  executeSubmit() {
    const isCreate = this.isOrganisationCreate()
    const aiModelId = this.state.selectedAiModel
      ? this.state.selectedAiModel.value
      : null
    const bot = isCreate
      ? {
          name: this.state.name,
          ...(this.props.isQcs
            ? { isQcs: true, packageTier: this.state.tier }
            : { aiModelId }),
        }
      : {
          name: this.state.name,
          aiModelId,
          dialogflowDevToken: this.state.developerToken,
          dialogflowClientToken: this.state.clientToken,
        }

    this.toggleLoader()
    if (isCreate) {
      createNewBotAPI(
        bot,
        created => {
          const finish = () => {
            this.setState({ showLoader: false })
            if (this.props.onCreated) this.props.onCreated()
            this.props.onClose()
          }
          const botId = created?.id || created?.data?.id
          const packageId = this.state.tier
          if (!(this.props.isQcs && botId && packageId)) {
            finish()
            return
          }
          assignOrChangeBotPackage(botId, packageId)
            .then(assignment => {
              cacheBotPackageAssignment(botId, assignment)
            })
            .catch(err => {
              alertError(
                err?.message || 'Organisation created, but the package could not be assigned.',
              )
            })
            .finally(finish)
        },
        text => this.errorHandler(text),
      )
    } else {
      cloneBot(
        this.props.botId,
        bot,
        () => {
          if (this.props.onCloned) this.props.onCloned()
          this.props.onClose()
        },
        text => this.errorHandler(text),
      )
    }
  }

  handleChangeType(withDialogFlow) {
    const newState = { withDialogFlow }
    if (withDialogFlow === 'false') {
      newState.clientToken = ''
      newState.developerToken = ''
      newState.botError = false
    }
    this.setState({ ...newState })
  }

  render() {
    const { classes } = this.props
    const isCreate = this.isOrganisationCreate()
    const showAiModel = this.needsAiModel()

    return (
      <div>
        <Input
          title="Enter new chatbot name"
          data-autotest={'enter-new-chatbot-name-input'}
          value={this.state.name}
          maxLength={255}
          // Match QCS / prototype field chrome: text sits close to the edge.
          style={{ paddingLeft: 12, paddingRight: 12 }}
          error={this.state.botError && isStringEmpty(this.state.name)}
          placeholder="Enter bot name here"
          onChange={event =>
            this.setState({
              name: event.target.value,
              botError: isStringEmpty(event.target.value),
            })
          }
        />

        {/* CareBrain create + clone: AI model. QCS create uses package instead. */}
        {showAiModel && (
          <div className={classes.aiModelSelect}>
            <Select
              label="AI model selection"
              required
              placeholder="Select AI model"
              options={this.state.aiModels.map(model => ({
                label: model.modelName,
                value: model.id,
              }))}
              value={this.state.selectedAiModel}
              onChange={option =>
                this.setState({ selectedAiModel: option, aiModelError: false, botError: false })
              }
              isDisabled={!this.props.new}
              error={this.state.aiModelError}
              errorMessage="Please select an AI model"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: base => ({ ...base, zIndex: 10001 }),
                valueContainer: base => ({ ...base, paddingLeft: 0 }),
              }}
            />
          </div>
        )}

        {isCreate && this.props.isQcs && (
          <TierSelect
            value={this.state.tier}
            onChange={tier => this.setState({ tier, botError: false })}
            error={this.state.botError && isStringEmpty(this.state.tier)}
          />
        )}

        {this.state.botError && (
          <p className={classes.error}>{this.state.botErrorText}</p>
        )}

        <div className={isCreate ? classes.buttonHolderCreate : classes.buttonHolder}>
          {!isCreate && (
            <SubmitButton
              type="primary_with_border"
              title="Cancel"
              onClick={this.props.onClose}
              dataAutotest={'create-new-bot-cancel-button'}
            />
          )}
          <SubmitButton
            title={this.state.buttonName}
            onClick={() => this.onSubmit()}
            dataAutotest={'create-new-bot-submit-button'}
          />
        </div>
        <LoaderSmall showLoader={this.state.showLoader} />

        {!isCreate && (
          <Modal
            open={this.state.showConfirmModal}
            onClose={this.closeConfirmModal}
            classes={{ paper: classes.confirmModalPaper }}>
            <CloneBotConfirmModal
              modelName={
                this.state.selectedAiModel && this.state.selectedAiModel.label
              }
              onConfirm={this.handleConfirmClone}
              onCancel={this.closeConfirmModal}
            />
          </Modal>
        )}
      </div>
    )
  }
}

NewBotModal.propTypes = {
  classes: PropTypes.object,
  isQcs: PropTypes.bool,
  new: PropTypes.bool,
  onCreated: PropTypes.func,
  onCloned: PropTypes.func,
}

const mapStateToProps = state => ({
  bots: state.bots,
})

export default withStyles(styles, { withTheme: true })(
  connect(mapStateToProps)(NewBotModal),
)
